import { Directive, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Input } from '@angular/core';

import { GenericUtils } from '@unpispas/upp-defs';

/**
 * Normalizes a host id and exposes it as DOM attributes for E2E and styling.
 *
 * Prefer property binding through this directive (`[uppDataid]="expr"` or static
 * `uppDataid="…"`). Property binding alone does not keep `uppdataid` on the DOM;
 * this directive writes both `uppdataid` and `data-id` with the normalized value.
 * The host must be a real DOM element (`div`, `ion-button`, a component root),
 * not `ng-container` or `ng-template`.
 *
 * @example
 * <div uppDataid="My Custom Text"></div>
 * <!-- Result: <div uppdataid="my_custom_text" data-id="my_custom_text"></div> -->
 *
 * @example
 * <button [uppDataid]="'tabtables-floor-thumb-' + (area.name || '')"></button>
 */
@Directive({
    selector: '[uppDataid]'
})
export class DataIdDirective implements OnInit, OnChanges {
    private _id: string | null = null;

    /**
     * Input property that accepts the text to be normalized.
     */
    @Input('uppDataid') set id(value: string | null) {
        this._id = value;
        this._apply();
    }
    get id(): string | null {
        return this._id;
    }

    constructor(private el: ElementRef<HTMLElement>) {}

    ngOnInit(): void {
        this._apply();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['id']) {
            this._apply();
        }
    }

    private _apply(): void {
        const value = this._id ? GenericUtils.Normalize(this._id) : null;
        const host = this.el?.nativeElement;
        if (!host) {
            return;
        }

        if (value) {
            host.setAttribute('uppdataid', value);
            host.setAttribute('data-id', value);
        }
        else {
            host.removeAttribute('uppdataid');
            host.removeAttribute('data-id');
        }
    }
}
